import { capitalize } from '@repo-level/core-front';
import { device } from 'components/styles/MediaQuery';
import type { Validator } from 'helpers/validators';
import { required } from 'helpers/validators';
import { useFieldState } from 'informed';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { Select } from './Select';

import { ScError, ScInputStructure } from './structures/styled';

const days = Array.apply(null, Array(31)).map((_, index) => {
  const day = index + 1;
  const dayFull = day > 9 ? `${day}` : `0${day}`;

  return {
    name: dayFull,
    value: dayFull,
  };
});

const months = Array.apply(null, Array(12)).map((_, index) => {
  const month = index + 1;

  return {
    name: month,
    value: (month > 9 ? `${month}` : `0${month}`).toString(),
  };
});

const getYears = () => {
  const currentFullYear = new Date().getFullYear();
  const currentYear = currentFullYear - 18;

  return Array.apply(null, Array(100)).map((_, index) => {
    const year = (Number(currentYear) - index).toString();

    return {
      name: year,
      value: year,
    };
  });
};

const years = getYears();

interface InputDateProps {
  field: string;
  validate?: Validator<any, any>;
  initialValue?: string;
  disabled?: boolean;
}

export const InputDate: FC<InputDateProps> = ({
  field,
  validate,
  initialValue,
  disabled,
}) => {
  const { t } = useTranslation();

  const [initialValueYear, initialValueMonth, initialValueDay] = useMemo(() => {
    if (initialValue == null) return [undefined, undefined, undefined];

    return initialValue.split('-');
  }, [initialValue]);

  const stateDay = useFieldState(`${field}.day`);
  const stateMonth = useFieldState(`${field}.month`);
  const stateYear = useFieldState(`${field}.year`);

  const date = useMemo(() => {
    if (stateDay == null || stateMonth == null || stateYear == null) return;

    if (
      stateDay.value == null ||
      stateMonth.value == null ||
      stateYear.value == null
    ) {
      return;
    }

    return `${stateDay.value}/${stateMonth.value}/${stateYear.value}`;
  }, [stateDay, stateMonth, stateYear]);

  const allFilled = Boolean(
    stateDay?.value && stateMonth?.value && stateYear?.value
  );

  const isError = (value: NonNullable<typeof stateDay>['value'] | undefined) =>
    errorField && (!value || allFilled) ? errorField : undefined;

  const errorField = useMemo(() => {
    if (validate == null) return;

    if (stateDay == null || stateMonth == null || stateYear == null) return;

    if (
      stateDay.touched == null ||
      stateMonth.touched == null ||
      stateYear.touched == null
    ) {
      return;
    }

    return validate(date, undefined) as string;
  }, [date, stateDay, stateMonth, stateYear, validate]);

  const monthsWithTrans = useMemo(
    () =>
      months.map(({ name, ...rest }) => ({
        ...rest,
        name: capitalize(t(`common:month.${name - 1}`)),
      })),
    [t]
  );

  return (
    <ScInputStructure>
      <ScInputDayOfBirthday>
        <Select
          animatedPlaceholder={t('glossary:date.dd')}
          disabled={disabled}
          error={isError(stateDay?.value)}
          field={`${field}.day`}
          hideErrorMessage
          initialValue={initialValueDay}
          options={days}
          validate={required}
          validateOnBlur
          withBorderMode
        />
        <Select
          animatedPlaceholder={t('glossary:date.mm')}
          disabled={disabled}
          error={isError(stateMonth?.value)}
          field={`${field}.month`}
          hideErrorMessage
          initialValue={initialValueMonth}
          options={monthsWithTrans}
          validate={required}
          validateOnBlur
          withBorderMode
        />
        <Select
          animatedPlaceholder={t('glossary:date.yyyy')}
          disabled={disabled}
          error={isError(stateYear?.value)}
          field={`${field}.year`}
          hideErrorMessage
          initialValue={initialValueYear}
          options={years}
          validate={required}
          validateOnBlur
          withBorderMode
        />
      </ScInputDayOfBirthday>
      {errorField && <ScError>{t(errorField as string)}</ScError>}
    </ScInputStructure>
  );
};

const ScInputDayOfBirthday = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;

  @media (${device.mobileS}) {
    grid-template-columns: 1fr;
  }
`;
